import React, {
  Suspense,
  useCallback,
  useState,
  useMemo,
  useEffect,
} from "react";
import { ScrollView, View, StyleSheet } from "react-native";
import Header from "./Common/Header";
import { StackActions, useNavigation } from "@react-navigation/native";
import { navigateToScreen } from "../../../utility/NavigationHelper";
import { AppScreens } from "../../Utility/AppScreens";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import StreamSetupHeader from "../../../Components/stream-setup-components/StreamSetupHeader";
import Separator from "../../../Components/Separator";
import StreamerView from "../../../Components/stream-setup-components/StreamerViewOptimized";
import { useLiveStream } from "../../../context/LiveStreamContext";
import {
  FetchTwitchUrlKey,
  FetchYouTubeUrlKey,
} from "../../../services/eventsAPI";
import { useWsProvider } from "../../../context/NativeApplicationContext";
import { toast } from "react-toastify";
import MatchPreviewComponent from "../../../Components/stream-setup-components/MatchPreviewComponent";
import StreamControlPanel from "../../../Components/stream-setup-components/StreamControlPanel";
import ChatPanel from "../../../Components/stream-setup-components/ChatPanel";
import StreamSetupSkeleton from "../../../Components/skeleton-components/StreamSetupSkeleton";
import { AppColors } from "../../../utility/AppColors";
import { useStreamSetupInit } from "./hooks/useStreamSetupInit";
import { useStreamModals } from "./hooks/useStreamModals";
import { useStreamerSocket } from "../../../utility/hooks/useStreamerSocket";
import { ScreenBGGradientView } from "../../../common/CommonView";

const EvidencePopup = React.lazy(
  () => import("../../../Components/stream-setup-components/EvidencePopup")
);
const InviteSocialMediaModal = React.lazy(
  () => import("../Event/InviteSocialMediaModal")
);
const AddSourcesPopup = React.lazy(
  () => import("../../../Components/stream-setup-components/AddSources")
);
const GreatEffortModal = React.lazy(
  () => import("../../../Components/stream-setup-components/GreatEffortModal")
);
const WinnerModal = React.lazy(() => import("./Modals/WinnerModal"));
const DisputeOccurredModal = React.lazy(
  () =>
    import("../../../Components/home-common-components/DisputeOccurredPopup")
);
const EventCompletedModal = React.lazy(
  () => import("../../../Components/home-common-components/EventCompletedPopup")
);
const ResultSubmissionModal = React.lazy(
  () =>
    import("../../../Components/home-common-components/Result-submission-popup")
);

interface StreamSetupProps {
  route?: {
    params?: {
      eventId?: string;
    };
  };
}

type ExpandedChat = "event" | "participants" | null;

interface ModalStates {
  evidencePopup: boolean;
  greatEffortPopup: boolean;
  addSourcePopup: boolean;
  showWinnerModal: boolean;
  isShare: boolean;
  isDisputeModalVisible: boolean;
  isEventCompletedModalVisible: boolean;
  isDisputeResultModalVisible: boolean;
  isManageSourcesPopupVisible: boolean;
}

const initialModalStates: ModalStates = {
  evidencePopup: false,
  greatEffortPopup: false,
  addSourcePopup: false,
  showWinnerModal: false,
  isShare: false,
  isDisputeModalVisible: false,
  isEventCompletedModalVisible: false,
  isDisputeResultModalVisible: false,
  isManageSourcesPopupVisible: false,
};

const StreamSetup: React.FC<StreamSetupProps> = ({ route }) => {
  const eventId = route?.params?.eventId;
  const navigation = useNavigation();
  const styles = useStyles();
  const deviceSize = useDeviceSize();

  const [expandedChat, setExpandedChat] = useState<ExpandedChat>(null);
  const [userData, setUserData] = useState(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isStreamStarting, setIsStreamStarting] = useState(false);
  const [streamJustStarted, setStreamJustStarted] = useState(false);
  const [modalStates, setModalStates] =
    useState<ModalStates>(initialModalStates);
  const [opponentRenderKey, setOpponentRenderKey] = useState(0);
  const [yourRenderKey, setYourRenderKey] = useState(0);

  React.useEffect(() => {
    if (!eventId) {
      toast.error("Event ID is required");
      navigation.goBack();
    }
  }, [eventId, navigation]);

  const {
    connectToEvent,
    organizerData,
    setOrganiserStreamStatus,
    setChallengerStreamStatus,
    setOrganiserLiveStart,
    setChallengerLiveStart,
    setOrganiserStreamCompleted,
    setChallengerStreamCompleted,
    setLoader,
    loader,
  } = useLiveStream();

  const {
    streamStarted,
    startStreaming,
    stopStreaming,
    startRecording,
    toggleRecording,
    isNativeAppConnected,
    canStartStreaming,
    canStopStreaming,
  } = useWsProvider();

  const updateModalState = useCallback(
    (key: keyof ModalStates, value: boolean) => {
      setModalStates((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const closeAllModals = useCallback(() => {
    setModalStates(initialModalStates);
  }, []);

  useStreamSetupInit({
    eventId: eventId || "",
    loading,
    userData,
    setUserData,
    setAccessToken,
    setUserRole,
    navigation,
    setLoader,
    connectToEvent,
    setOrganiserStreamStatus,
    setChallengerStreamStatus,
    setOrganiserLiveStart,
    setChallengerLiveStart,
    setOrganiserStreamCompleted,
    setChallengerStreamCompleted,
  });

  useStreamModals({ organizerData, userData, updateModalState });

  // Determine if we have a valid streamer role (not null, not audience)
  const validStreamerRole =
    userRole === "organiser" || userRole === "challenger" ? userRole : null;

  // Socket integration for real-time stream events
  // Only connects when we have a valid role (prevents race condition on refresh)
  const { isOpponentLive, isOpponentPaused, opponentStatus } =
    useStreamerSocket({
      eventId: validStreamerRole ? eventId || "" : "", // Empty eventId prevents connection
      username: validStreamerRole ? (userData as any)?.username || "" : "",
      role: (validStreamerRole || "organiser") as "organiser" | "challenger",
      accessToken: accessToken || undefined,
    });

  // Watch opponent status changes and trigger player reload with retry logic
  const prevOpponentStatusRef = React.useRef(opponentStatus);
  const retryTimeoutsRef = React.useRef<NodeJS.Timeout[]>([]);

  useEffect(() => {
    const prevStatus = prevOpponentStatusRef.current;

    // Clear any existing retry timeouts
    const clearRetryTimeouts = () => {
      retryTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      retryTimeoutsRef.current = [];
    };

    // Reload player when opponent goes live or reconnects
    if (
      (prevStatus !== "live" && opponentStatus === "live") ||
      (prevStatus === "paused" && opponentStatus === "live")
    ) {
      console.log(
        "[StreamSetup] Opponent went live - starting reload sequence"
      );
      toast.success("Opponent stream is now live!");

      // Clear any previous retry timeouts
      clearRetryTimeouts();

      // Reload at 5s interval to handle stream propagation delay
      const retryIntervals = [7000]; // 15000 commented out for now
      retryIntervals.forEach((delay, index) => {
        const timeout = setTimeout(() => {
          console.log(
            `[StreamSetup] Retry reload #${index + 1} at ${delay / 1000}s`
          );
          toast.info(`Refreshing opponent stream (attempt ${index + 1})`);
          setOpponentRenderKey((prev) => prev + 1);
        }, delay);
        retryTimeoutsRef.current.push(timeout);
      });
    }

    // Toast when opponent pauses
    if (prevStatus === "live" && opponentStatus === "paused") {
      toast.warning("Opponent stream paused - waiting for reconnection...");
    }

    // Clear timeouts if opponent goes offline/paused
    if (opponentStatus !== "live" && prevStatus === "live") {
      clearRetryTimeouts();
    }

    prevOpponentStatusRef.current = opponentStatus;

    // Cleanup on unmount
    return () => {
      clearRetryTimeouts();
    };
  }, [opponentStatus]);

  // Watch for your own stream starting and trigger reload with retry logic
  const prevStreamStartedRef = React.useRef(streamStarted);
  const yourRetryTimeoutsRef = React.useRef<NodeJS.Timeout[]>([]);

  useEffect(() => {
    const wasStreaming = prevStreamStartedRef.current;

    // Clear any existing retry timeouts
    const clearYourRetryTimeouts = () => {
      yourRetryTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      yourRetryTimeoutsRef.current = [];
    };

    // Reload your preview when you start streaming
    if (!wasStreaming && streamStarted) {
      console.log(
        "[StreamSetup] Your stream started - starting reload sequence"
      );

      // Clear any previous retry timeouts
      clearYourRetryTimeouts();

      // Reload at 5s and 15s intervals to handle stream propagation delay
      const retryIntervals = [7000]; // 15000 commented out for now
      retryIntervals.forEach((delay, index) => {
        const timeout = setTimeout(() => {
          console.log(
            `[StreamSetup] Your stream retry reload #${index + 1} at ${
              delay / 1000
            }s`
          );
          toast.info(`Refreshing your stream (attempt ${index + 1})`);
          setYourRenderKey((prev) => prev + 1);
        }, delay);
        yourRetryTimeoutsRef.current.push(timeout);
      });
    }

    // Clear timeouts if stream stops
    if (!streamStarted && wasStreaming) {
      clearYourRetryTimeouts();
    }

    prevStreamStartedRef.current = streamStarted;

    // Cleanup on unmount
    return () => {
      clearYourRetryTimeouts();
    };
  }, [streamStarted]);

  const toggleStream = useCallback(async (): Promise<boolean> => {
    if (!isNativeAppConnected) {
      toast.error("Please connect to KO Broadcaster first.");
      return false;
    }

    if (!canStartStreaming && !canStopStreaming) {
      toast.error("Cannot toggle stream in current state.");
      return false;
    }

    if (streamStarted) {
      stopStreaming();
      return true;
    }

    try {
      setLoading(true);
      setIsStreamStarting(true);

      let youtubeKey = "";
      let twitchKey = "";

      const platforms =
        organizerData?.eventDetails?.streamingPlatforms?.[userRole || ""] || [];

      if (platforms.includes("YouTube")) {
        const ytResp = await FetchYouTubeUrlKey(accessToken, eventId);
        if (ytResp.statusCode === 200 || ytResp.statusCode === 201) {
          if (ytResp.body?.StreamKey) {
            youtubeKey = ytResp.body.StreamKey;
          } else {
            throw new Error("No YouTube stream key found");
          }
        } else {
          throw new Error(ytResp.body?.error || "Failed to fetch YouTube key");
        }
      }

      if (platforms.includes("Twitch")) {
        const twitchResp = await FetchTwitchUrlKey(accessToken, eventId);
        if (twitchResp.statusCode === 200 || twitchResp.statusCode === 201) {
          if (twitchResp.body?.StreamKey) {
            twitchKey = twitchResp.body.StreamKey;
          } else {
            throw new Error("No Twitch stream key found");
          }
        } else {
          throw new Error(
            twitchResp.body?.error || "Failed to fetch Twitch key"
          );
        }
      }

      await startStreaming(youtubeKey || undefined, twitchKey || undefined);
      toast.success("Stream started successfully.");
      setStreamJustStarted(true);
      return true;
    } catch (err: any) {
      toast.error(`Error starting stream: ${err.message}`);
      return false;
    } finally {
      setLoading(false);
      setIsStreamStarting(false);
    }
  }, [
    isNativeAppConnected,
    canStartStreaming,
    canStopStreaming,
    streamStarted,
    startStreaming,
    stopStreaming,
    accessToken,
    eventId,
    organizerData,
    userRole,
  ]);

  const handleEventChatExpand = useCallback((expand: boolean) => {
    setExpandedChat(expand ? "event" : null);
  }, []);

  const handleParticipantChatExpand = useCallback((expand: boolean) => {
    setExpandedChat(expand ? "participants" : null);
  }, []);

  const handleFileDispute = useCallback(() => {
    toast.info("Dispute filed successfully");
    updateModalState("isDisputeResultModalVisible", false);
  }, [updateModalState]);

  const navigateToDrawer = useCallback(() => {
    navigateToScreen(navigation, AppScreens.DrawerRoute);
  }, [navigation]);

  const navigateToSearch = useCallback(() => {
    navigateToScreen(navigation, AppScreens.SearchEventWebScreen);
  }, [navigation]);

  const navigateToProfile = useCallback(() => {
    navigateToScreen(navigation, AppScreens.MyProfileWebScreen);
  }, [navigation]);

  const isSmallScreen = useMemo(
    () => deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM,
    [deviceSize]
  );

  const containerPadding = isSmallScreen ? 10 : 20;
  const separatorMarginTop = isSmallScreen ? 10 : 20;

  if (!eventId) {
    return null;
  }

  return (
    <View style={styles.container}>
      <ScreenBGGradientView />
      <Header
        btnSearchTapped={navigateToSearch}
        btnWalletTapped={() => {}}
        btnStartTutorialTapped={() => {}}
        btnMessageTapped={() => {}}
        btnNotificationTapped={() => {}}
        btnProfileTapped={() => {}}
      />

      {loader ? (
        <StreamSetupSkeleton showLoader={loader} />
      ) : (
        <>
          <View style={{ padding: containerPadding }}>
            <StreamSetupHeader eventDetail={organizerData?.eventDetails} />
            <Separator
              color={AppColors.black_2D2733}
              height={1}
              marginTop={separatorMarginTop}
              marginBottom={0}
              marginHorizontal={0}
            />
          </View>

          <ScrollView
            contentContainerStyle={{ flexGrow: 1 }}
            style={styles.scrollView}
            showsVerticalScrollIndicator={false}
          >
            <View style={{ padding: containerPadding, paddingTop: 0 }}>
              <View style={{ gap: 20 }}>
                <View
                  style={{
                    flexDirection: isSmallScreen ? "column" : "row",
                    justifyContent: "space-between",
                    gap: 20,
                  }}
                >
                  <StreamerView
                    setIsShare={(value) => updateModalState("isShare", value)}
                    isManageSourcesPopupVisible={
                      modalStates.isManageSourcesPopupVisible
                    }
                    onManageSourcesPress={() =>
                      updateModalState(
                        "isManageSourcesPopupVisible",
                        !modalStates.isManageSourcesPopupVisible
                      )
                    }
                  />

                  <View
                    style={[
                      { width: isSmallScreen ? "100%" : "25%" },
                      expandedChat === "event" ? styles.fullScreenChat : {},
                    ]}
                  >
                    <ChatPanel
                      variant="event"
                      setIsExpandChat={handleEventChatExpand}
                      isExpandChat={expandedChat === "event"}
                    />
                  </View>

                  <View
                    style={[
                      { width: isSmallScreen ? "100%" : "25%" },
                      expandedChat === "participants"
                        ? styles.fullScreenChat
                        : {},
                    ]}
                  >
                    <ChatPanel
                      variant="participants"
                      setIsExpandChat={handleParticipantChatExpand}
                      isExpandChat={expandedChat === "participants"}
                    />
                  </View>
                </View>

                {!isSmallScreen && (
                  <View style={styles.previewControlSection}>
                    <View style={{ flexDirection: "column", width: "73.5%" }}>
                      <MatchPreviewComponent
                        userData={userData}
                        streamJustStarted={streamJustStarted}
                        onStreamStarted={() => setStreamJustStarted(false)}
                        opponentStatus={opponentStatus}
                        opponentRenderKey={opponentRenderKey}
                        yourRenderKey={yourRenderKey}
                      />
                    </View>
                    <StreamControlPanel
                      startStream={streamStarted}
                      toggleStream={toggleStream}
                      startRecording={startRecording}
                      toggleRecording={toggleRecording}
                      accessToken={accessToken || ""}
                      isStreamStarting={isStreamStarting}
                      loading={loading}
                      updateModalState={updateModalState}
                    />
                  </View>
                )}
              </View>
            </View>
          </ScrollView>

          {modalStates.evidencePopup && (
            <Suspense fallback={null}>
              <EvidencePopup
                evidencePopup={modalStates.evidencePopup}
                setEvidencePopup={(value) => {
                  updateModalState("evidencePopup", value);
                  if (!value) navigateToDrawer();
                }}
              />
            </Suspense>
          )}

          {modalStates.greatEffortPopup && (
            <Suspense fallback={null}>
              <GreatEffortModal
                visible={modalStates.greatEffortPopup}
                onClose={() => {
                  updateModalState("greatEffortPopup", false);
                  navigateToDrawer();
                }}
                onExploreMoreEvents={() => {
                  updateModalState("greatEffortPopup", false);
                  navigateToDrawer();
                }}
              />
            </Suspense>
          )}

          {modalStates.addSourcePopup && (
            <Suspense fallback={null}>
              <AddSourcesPopup
                addSourcePopup={modalStates.addSourcePopup}
                setAddSourcePopup={(value) =>
                  updateModalState("addSourcePopup", value)
                }
              />
            </Suspense>
          )}

          {modalStates.showWinnerModal && (
            <Suspense fallback={null}>
              <WinnerModal
                closeWinnerModal={() => {
                  updateModalState("showWinnerModal", false);
                  navigateToDrawer();
                }}
                openWinnerModal={modalStates.showWinnerModal}
                eventDetails={organizerData?.eventDetails}
                winnerDetails={organizerData?.winnerDetails}
              />
            </Suspense>
          )}

          {modalStates.isShare && (
            <Suspense fallback={null}>
              <InviteSocialMediaModal
                accessToken={accessToken || ""}
                challengeDetails={{}}
                showModal={modalStates.isShare}
                setShowModal={(value) => updateModalState("isShare", value)}
                eventType="StreamSetup"
                eventID={eventId}
                status=""
              />
            </Suspense>
          )}

          {modalStates.isDisputeModalVisible && (
            <Suspense fallback={null}>
              <DisputeOccurredModal
                visible={modalStates.isDisputeModalVisible}
                onClose={() => updateModalState("isDisputeModalVisible", false)}
                accessToken={accessToken || ""}
                organizerData={organizerData}
                userData={userData}
                setDisputeResultModalVisible={(value) =>
                  updateModalState("isDisputeResultModalVisible", value)
                }
                setEvidencePopup={(value) =>
                  updateModalState("evidencePopup", value)
                }
              />
            </Suspense>
          )}

          {modalStates.isEventCompletedModalVisible && (
            <Suspense fallback={null}>
              <EventCompletedModal
                visible={modalStates.isEventCompletedModalVisible}
                onClose={() =>
                  updateModalState("isEventCompletedModalVisible", false)
                }
                accessToken={accessToken || ""}
                organizerData={organizerData}
                setDisputeResultModalVisible={(value) =>
                  updateModalState("isDisputeResultModalVisible", value)
                }
              />
            </Suspense>
          )}

          {modalStates.isDisputeResultModalVisible && (
            <Suspense fallback={null}>
              <ResultSubmissionModal
                visible={modalStates.isDisputeResultModalVisible}
                onClose={() => {
                  updateModalState("isDisputeResultModalVisible", false);
                  navigateToDrawer();
                }}
                onFileDispute={handleFileDispute}
              />
            </Suspense>
          )}
        </>
      )}
    </View>
  );
};

export default StreamSetup;

const useStyles = CreateResponsiveStyle(
  {
    container: {
      flex: 1,
    },
    scrollView: {
      overflow: "scroll",
    },
    fullScreenChat: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: "rgba(0, 0, 0, 0.9)",
      zIndex: 999,
    },
    previewControlSection: {
      flexDirection: "row",
      height: 300,
      justifyContent: "space-between",
    },
  },
  {
    [DEVICE_SIZES.LG]: {},
    [DEVICE_SIZES.MD]: {},
    [DEVICE_SIZES.SM]: {},
    [DEVICE_SIZES.XS]: {},
  }
);
